import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { SubSink } from 'subsink';
import { DragPart } from './interaction/movement/drag-and-resize/drag-part';
import { ITimeBlockComponentItem } from './types/time-block-component-item-types';
import { CalendarService } from '../../services/calendar.service';
import { TimeBlockGeometryService } from './calculation/geometry/time-block-geometry.service';
import { CalendarEvents, CalendarViews } from '../../calendar-types';
import { DragHandlerService } from './interaction/movement/drag-and-resize/drag-handler.service';
import { TimeBlockItemBuilderService } from './generation/time-block-item-builder.service';
import { BaseCalendarModel } from '../../../../core/models/calendar/base-calendar.model';
import { TimeBlockDragResizeService } from './interaction/movement/drag-and-resize/time-block-drag-resize-handling/time-block-drag-resize.service';
import { TimeBlockContentType, TimeBlockType } from './types/time-block-types';
import {
  TimeBlockDayOrWeekVariableIntervalType,
  TimeBlockMonthVariableIntervalType,
} from '../../../../core/models/timeblock/time-block-view-type.model';
import { TimeBlockService } from './services/time-block.service';
import { CalendarServiceHelper } from '../../services/calendar-service-helper';
import {
  CSSDefaultTimeBlock,
  CSSFixIntervalTimeBlockOverflowsLeftClass,
  CSSFixIntervalTimeBlockOverflowsRightClass,
  CSSHasNextPart,
  CSSHasPrevPart,
  CSSHeadPart,
  CSSTrackedTimeBlock,
  FixIntervalTimeBlockHeightPx,
} from '../../util/calendar-style.constants';
import { DragEdgeTemplateComponent } from './templates/drag-edge-template/drag-edge-template.component';
import { TimeBlockDialogService } from './services/time-block-dialog.service';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { TimeBlockCSSService } from './services/time-block-css.service';

/**
 * Day and week inner day: Each lane has a separate time block component stored in the calendar model for each day from start to end.
 * Full day: Consists of only one time block in the calendar model from start to end.
 */
@Component({
  selector: 'app-time-block',
  templateUrl: './time-block.component.html',
  styleUrls: ['./time-block.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeBlockComponent implements AfterViewInit, OnDestroy {
  @ViewChild('timeBlock') public timeBlockHTMLWrapper: ElementRef<HTMLElement>;
  @ViewChildren('overflowIndicator') public overflowIndicators: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren(DragEdgeTemplateComponent) dragEdgeTemplates: QueryList<DragEdgeTemplateComponent>;
  public timeBlockBody: ElementRef<HTMLElement>;
  public timeBlockComponentItem: ITimeBlockComponentItem;
  public DragPart = DragPart;
  public TimeBlockType = TimeBlockType;
  public TimeBlockContentType = TimeBlockContentType;
  public CalendarView = CalendarViews;
  public calendarViewMode: CalendarViews;
  public CSSFixIntervalTimeBlockOverflowsLeftClass = CSSFixIntervalTimeBlockOverflowsLeftClass;
  public CSSFixIntervalTimeBlockOverflowsRightClass = CSSFixIntervalTimeBlockOverflowsRightClass;
  public fixIntervalTimeBlockHeight = FixIntervalTimeBlockHeightPx;
  public readonly CSSHasPrevPart = CSSHasPrevPart;
  public readonly CSSHasNextPart = CSSHasNextPart;
  public readonly faPenToSquare = faPenToSquare;
  public readonly CSSHeadPart = CSSHeadPart;
  public readonly CSSTrackedTimeBlock = CSSTrackedTimeBlock;
  public readonly CSSDefaultTimeBlock = CSSDefaultTimeBlock;
  public readonly cd = inject(ChangeDetectorRef);
  public readonly timeBlockService = inject(TimeBlockService);
  public readonly timeBlockCSSService = inject(TimeBlockCSSService);
  private readonly timeBlockDragResizeService = inject(TimeBlockDragResizeService);
  private readonly dragHandlerService = inject(DragHandlerService);
  private readonly calendarService = inject(CalendarService);
  private readonly timeBlockDialogService = inject(TimeBlockDialogService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly timeBlockGeometryService = inject(TimeBlockGeometryService);
  private readonly subs = new SubSink();

  ngAfterViewInit(): void {
    const tbModel = this.timeBlockComponentItem.timeBlockModel;

    // Don't initialize ghost blocks, just render them.
    if (tbModel.type === TimeBlockType.GhostBlock) {
      return;
    }

    this.cd.detach();

    this.calendarViewMode = this.calendarService.model.calendarViewMode;
    // Re-render time block to update the height.
    this.cd.detectChanges();
    this.initTimeBlock();

    // For month view inner day time blocks we don't want to drag or resize a newly created non-existing one. Just render it and open the
    // related dialog.
    if (
      tbModel.timeBlockViewType instanceof TimeBlockMonthVariableIntervalType &&
      tbModel.type === TimeBlockType.NonExistingBlock
    ) {
      this.timeBlockDialogService.openNewTimeBlockDialog(this.timeBlockComponentItem);
      return;
    }

    this.initEvents();
    this.initDragAndResizeBehaviour();
  }

  // Unsubscribe when the component dies
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initTimeBlock(): void {
    const tbModel = this.timeBlockComponentItem.timeBlockModel;
    if (tbModel.type === TimeBlockType.GhostBlock) {
      return;
    }

    this.timeBlockGeometryService.calculateInitialTimeBlockGeometry(this.timeBlockComponentItem);
    this.dragHandlerService.configureDragHandlers(this.timeBlockComponentItem);
    if (tbModel.fixInterval === null) {
      this.timeBlockCSSService.setTimeBlockPrevNextCSSClasses(
        this.timeBlockComponentItem,
        this.timeBlockHTMLWrapper,
      );
    }
    this.cd.detectChanges();
  }

  private initEvents(): void {
    // As soon as a user starts to drag / resize this block, update ui via change detection.
    const targetEvents = [CalendarEvents.AddedTimeBlock, CalendarEvents.ReplacedTimeBlock];
    const callback = (
      _: BaseCalendarModel,
      _1: CalendarEvents[],
      timeBlock: ITimeBlockComponentItem,
    ): void => {
      if (!timeBlock) {
        throw new Error('Time block not specified.');
      }
      const timeBlockModel = this.timeBlockComponentItem.timeBlockModel;
      if (
        timeBlockModel.type === TimeBlockType.GhostBlock ||
        timeBlock.timeBlockModel.type === TimeBlockType.GhostBlock
      ) {
        return;
      }

      if (timeBlockModel.id === timeBlock.timeBlockModel.id) {
        // Make sure we set the correct time block since the time block ids can exist multiple times.
        if (timeBlockModel.partNumber !== timeBlock.timeBlockModel.partNumber) {
          return;
        }
        // Clone the time block to immediately set the new time block geometry data.
        this.timeBlockComponentItem = timeBlock.clone(this.timeBlockItemBuilderService);
        // Add extra rendering cycle to update hours and minutes on the time blocks after drag / resize.
        this.cd.detectChanges();
      }
    };

    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calendarService,
      callback,
      targetEvents,
    );
  }

  private initDragAndResizeBehaviour(): void {
    this.timeBlockCSSService.configureFixIntervalOverflowIndicators(
      this.timeBlockComponentItem,
      this.overflowIndicators,
    );

    // Make non-transparent blocks draggable and resizable
    this.timeBlockDragResizeService.initialize(this.timeBlockComponentItem);

    // If block is a new block (non-existing), make it resizable
    this.makeNonExistingTimeBlockResizable();
  }

  private makeNonExistingTimeBlockResizable(): void {
    // Don't make month view inner day time blocks resizable.
    if (
      this.timeBlockComponentItem.timeBlockModel.timeBlockViewType instanceof
      TimeBlockMonthVariableIntervalType
    ) {
      return;
    }

    if (this.timeBlockComponentItem.timeBlockModel.type === TimeBlockType.NonExistingBlock) {
      const timeBlockDragResizeController =
        this.timeBlockDragResizeService.timeBlockDragResizeControllerService;
      timeBlockDragResizeController.TransformationTimeBlock = this.timeBlockComponentItem;
      // Vertical resizing
      if (
        this.timeBlockComponentItem.timeBlockModel.timeBlockViewType instanceof
        TimeBlockDayOrWeekVariableIntervalType
      ) {
        // If dragPart is already set from previous time block part, set drag part with this value.
        const dragPart =
          timeBlockDragResizeController.DragPart === DragPart.None
            ? DragPart.TopOrBottom
            : timeBlockDragResizeController.DragPart;
        this.timeBlockDragResizeService.verticalDayOrWeekResizeControllerService.resizeStarted$.next(
          dragPart,
        );
      } else {
        // Horizontal resizing
        // If dragPart is already set from previous time block part, set drag part with this value.
        const dragPart =
          timeBlockDragResizeController.DragPart === DragPart.None
            ? DragPart.LeftOrRight
            : timeBlockDragResizeController.DragPart;
        this.timeBlockDragResizeService.horizontalAllResizeControllerService.resizeStarted$.next(
          dragPart,
        );
      }
    }
  }
}
